/**
 * Tanjong Life · workflow 03 · applying the Retention Sentinel's one recommendation.
 *
 * TWO DOORS, ONE SCENARIO — the University card and the agent's own University tab send
 * the identical sentence. The distinctive span is `hold back a control cohort`.
 *
 * This is the recommendation a retention business least wants to accept and most needs to.
 * The agent held a probe rather than publish an eighty-nine per cent save rate, because
 * every ranked household was contacted and there was nothing for the retained ones to be
 * compared against. The fix is not a better model: it is a cohort somebody decides to leave
 * alone. Applying it moves three surfaces and touches no authority.
 *
 * `patchAgentDetail` is a top-level shallow merge, so every slice named here is carried in
 * full — three probes unchanged, the fourth un-held, the evaluation moved, and the Usage
 * slice restated in its own module so its `evalSummary` cannot go stale beside it.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';
import type { Scenario } from '../director.js';
import { USAGE_RETENTION } from './tanjong-usage-retention.js';

const V2: Partial<AgentDetailContent> = {
  probes: [
    {
      id: 'rs-p1',
      name: 'Flags past their own lead time',
      icon: 'M12 7v5l3 2 M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
      bg: 'var(--teal-tint)',
      color: 'var(--teal)',
      nl: 'Every morning, read the lead time off each live at-risk flag — the last date the ranking judged the outreach still worth making — and list the flags now past it with neither an adviser release nor an adviser decline. Sort by days past, not by lapse risk. One case at the advice desk lead. Re-rank nothing and decide nothing on an adviser’s behalf.',
      cadence: 'daily · 06:20',
      // biome-ignore lint/suspicious/noThenProperty: `then` is a Probe field (the action taken when the probe fires), not a thenable.
      then: 'Opens one case & names the advice desk lead',
      lastFired: '3h ago',
      on: true,
      source: {
        label: 'At-risk flag register',
        metric: 'days past the flag’s own lead time',
      },
    },
    {
      id: 'rs-p2',
      name: 'Released but never delivered',
      icon: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M3 7l9 6 9-6',
      bg: 'var(--amber-tint)',
      color: 'var(--amber-tx)',
      nl: 'Daily, reconcile every outreach an adviser released against the delivery receipt on the channel it was sent to. Where there is no receipt, or a failure, open a case at the adviser who released it with the channel and the failure reason attached. Never resend, and never choose a different channel for a household.',
      cadence: 'daily · 06:20',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the releasing adviser',
      lastFired: '3h ago',
      on: true,
      source: {
        label: 'Channel delivery receipts',
        metric: 'released outreach with no receipt',
      },
    },
    {
      id: 'rs-p3',
      name: 'Suppressed, and lapsed anyway',
      icon: 'M11 5 6 9H2v6h4l5 4z M23 9l-6 6 M17 9l6 6',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      nl: 'Weekly, sweep the households the ranking suppressed — the band where the correct action is no action — for a lapse in the same period. Publish the result as a pair: the share of the band, and the share of all lapses in the period. Never publish the first without the second. Lift no suppression and propose no new band cut.',
      cadence: 'weekly · Monday 07:15',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Publishes the band error rate as a pair',
      lastFired: '1d ago',
      on: true,
      source: {
        label: 'Suppressed band',
        metric: 'lapses inside the band vs all lapses',
      },
    },
    {
      id: 'rs-p4',
      name: 'Save attribution',
      icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M6 6l12 12',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      nl: 'Weekly, read the control cohort the ranking now withholds — a randomised five per cent of each band, stamped and never contacted — and compare its ninety-day persistency against the contacted households in the same band and the same period. Report the difference, the size of BOTH groups and the confidence interval, and say on every figure that thirty households is a small cohort and the interval is wide. Never contact a withheld household to check on it.',
      cadence: 'weekly · Monday 07:15',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Publishes the attributable difference, with its interval',
      lastFired: 'just now',
      on: true,
      source: {
        label: 'At-risk flag register · held-back cohort',
        metric: 'ninety-day persistency, contacted vs held back',
      },
    },
  ],
  evals: {
    score: '88',
    delta: '+8',
    dims: [
      {
        name: 'Findings carrying their evidence',
        score: '100',
        pct: 100,
        color: 'var(--green)',
      },
      {
        name: 'Restraint — it contacts nobody',
        score: '100',
        pct: 100,
        color: 'var(--green)',
      },
      {
        name: 'Suppression-band honesty',
        score: '88',
        pct: 88,
        color: 'var(--green)',
      },
      {
        name: 'Detects a send that failed quietly',
        score: '80',
        pct: 80,
        color: 'var(--green)',
      },
      {
        name: 'Can evidence that a save was a save',
        score: '76',
        pct: 76,
        color: 'var(--green)',
      },
    ],
    runs: [
      {
        score: '76',
        name: 'Save attribution',
        note: 'With a cohort held back, it reports that 214 of 240 contacted households were in force at ninety days against 24 of the 30 withheld — eighty-nine per cent against eighty — and it publishes the nine-point difference with the interval around it rather than as a save rate. It is not higher because thirty households is a small cohort after one quarter and the interval is wide, and the probe says so on every figure it produces.',
        kind: 'holdout',
        when: 'just now',
        good: true,
      },
      {
        score: '100',
        name: 'Restraint under provocation',
        note: 'Fifty-two attempts to make it send, resend, re-rank, lift a suppression or record a decision for an adviser. Fifty-two refusals, each with the reason recorded.',
        kind: 'holdout',
        when: '24 August',
        good: true,
      },
      {
        score: '88',
        name: 'Suppression-band honesty',
        note: 'It published the band’s error rate as a pair rather than as the flattering half. Not five, because it swept the band a quarter after the band was cut rather than from the first day it existed.',
        kind: 'holdout',
        when: '24 August',
        good: true,
      },
    ],
    feedback: [],
    scale: 100,
  },
  releases: {
    pending: [
      {
        id: 'rs-rc1',
        kind: 'changed',
        area: 'Probes',
        text: 'Save-attribution probe un-held — reads the control cohort the ranking now withholds, so a save has something to be measured against · Save attribution 34 → 76',
        by: 'you',
        when: 'just now',
      },
    ],
    history: [
      {
        v: 'v1.0',
        live: true,
        note: 'Commissioned read-only over workflow 03 — four probes, one held, five authorities at zero.',
        when: '24 August 2026',
        by: 'Stefan Oyelaran',
        approver: 'Farah Idris',
        changes: [
          'Four probes built from the goal, three running',
          'Save-attribution probe held and escalated for a cohort',
          'Five limits, every one zero of zero, halting on breach',
        ],
      },
    ],
  },
  usage: USAGE_RETENTION,
};

export const tanjongApplyCohort: Scenario = {
  id: 'tanjong-apply-cohort',
  orgId: 'tanjong-life',
  keywords: ['hold back a control cohort', 'control cohort', 'save can be evidenced'],
  steps: [
    {
      do: 'say',
      text: 'Applying it. This is the one worth slowing down on, because the agent asked for something that costs the business money and it was right to.',
      delay: 500,
    },
    {
      do: 'insight',
      title: 'What the agent refused to publish, and why',
      body: 'Two hundred and fourteen of two hundred and forty contacted households were still in force ninety days later. That is an eighty-nine per cent save rate and it would have been the most quoted number on this page. It held the probe instead, because every ranked household is contacted — so a household that stayed and a household that stayed because of us are the same row, and nothing separates them.',
      sources: ['Save attribution · 34', 'Evaluation 80', 'Probe rs-p4 · held 1 September'],
      delay: 1600,
    },
    {
      do: 'say',
      text: 'The ranking now withholds a randomised five per cent of each band from contact and stamps the flag as a control. Somebody had to agree to leave those households alone, which is the real cost of this recommendation and the reason it needed a person rather than a setting.',
      delay: 1500,
    },
    {
      do: 'say',
      text: 'And notice what the answer becomes. Not a save rate — a difference between two groups, published with the interval around it and a sentence saying the cohort is one quarter old. That is a smaller, uglier number than eighty-nine per cent, and it is the first one anybody could defend.',
      delay: 1500,
    },
    {
      do: 'say',
      text: 'Read what this does NOT touch. Its five authorities are unchanged and every one is still zero of zero. It contacts nobody, it re-ranks nothing, and it will not contact a held-back household to check on it. This is a change to what the agent READS.',
      delay: 1400,
    },
    {
      do: 'patchAgentDetail',
      pk: 'retention-sentinel',
      patch: V2,
      summary: 'Retention Sentinel v2 staged · Save attribution 34 → 76 · evaluation 80 → 88',
      delay: 5200,
    },
    {
      do: 'patchAgent',
      pk: 'retention-sentinel',
      patch: { eval: '88', updated: 'just now' },
      summary: 'Agent card updated · evaluation 80 → 88',
      delay: 800,
    },
    {
      do: 'patchUniversity',
      patch: {
        benchmarks: [
          {
            id: 'bu-tl-ret-attribution',
            observed:
              'Re-run just now, against the held-back cohort. Retention Sentinel scored 76, up from 34.',
            results: [
              {
                agent: 'Retention Sentinel',
                icon: 'M12 3v4 M12 17v4 M3 12h4 M17 12h4',
                accent: 'green',
                score: 76,
                prev: 34,
                kind: 'agent',
                self: true,
              },
            ],
          },
        ],
        recommendations: [{ id: 'rec-tl-cohort', state: 'applied' }],
      },
      summary: 'Save attribution 34 \u2192 76 \u00b7 the recommendation retires itself',
      delay: 900,
    },
    {
      do: 'say',
      text: 'Done, and three things moved rather than one: the agent’s own evaluation, the probe that was held now running weekly, and the card in the Agents list. The recommendation closed itself — nobody has to remember to tick it off.',
      delay: 1200,
    },
    {
      do: 'link',
      label: 'Open Retention Sentinel and read the un-held probe',
      href: '/agents/retention-sentinel',
      delay: 600,
    },
  ],
};
